import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import SentryInfiniteScroll from '../../components/ui/common/SentryInfiniteScroll';
import LoaderSmall from '../../components/ui/LoaderSmall';
import { GreaseTrapCard } from '../../components/ui/vendors/grease-traps/GreaseTrapCard';
import StatCard from '../../components/ui/vendors/StatCard';
import { useGreaseTrapList } from '../../hooks/useGreaseTrapList';
import { selectedVendor } from '../../recoil/navigation';
import { needRefreshState } from '../../recoil/needRefresh';
import {
  GET_GREASE_TRAP_OPTIONS,
  GET_GREASE_TRAP_STATS,
} from '../../services/graphql/vendors/grease-traps/greaseTrapQuery';
import type {
  GetGreaseTrapOptionsResponse,
  GetGreaseTrapStatsResponse,
} from '../../types/greaseTrap';
import { formatNumber, formatNumberMoney } from '../../utils/numberFormatter';
import SingleVendorLayout from './SingleVendorLayout';

const VendorGreaseTrapsView = () => {
  const { t } = useTranslation('common');
  const { vendor } = useParams();
  const vendorInfo = useRecoilValue(selectedVendor);
  const needsRefresh = useRecoilValue(needRefreshState);

  const { data: metrics, refetch: refetchStats } =
    useQuery<GetGreaseTrapStatsResponse>(GET_GREASE_TRAP_STATS, {
      skip: !vendor,
      variables: { vendorId: vendor },
    });

  const { data: options } = useQuery<GetGreaseTrapOptionsResponse>(
    GET_GREASE_TRAP_OPTIONS,
    {
      skip: !vendor,
      variables: { vendorId: vendor },
    },
  );

  const {
    fetchMoreGreaseTraps,
    greaseTraps,
    hasNextPage,
    loading,
    errorTimeline,
    loadingSingleQuery,
    refetchGreaseTraps,
    refetchSingleGreaseTrap,
  } = useGreaseTrapList({ vendorId: vendor });

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: fetchMoreGreaseTraps,
    disabled: !!errorTimeline,
    rootMargin: '0px 0px 20px 0px',
  });

  useEffect(() => {
    if (needsRefresh === 'grease-traps') {
      refetchGreaseTraps();
      refetchStats();
    }
  }, [needsRefresh, refetchGreaseTraps, refetchStats]);

  return (
    <SingleVendorLayout
      title={t('vendor-grease-traps.title')}
      breadcrumbs={t('vendor-grease-traps.title-breadcrumbs')}
    >
      <div className="flex w-full flex-col gap-y-2">
        <div className="w-full">
          <h2 className="text-xl font-semibold">
            {t('vendor-grease-traps.metrics-title')}
          </h2>
          <div className="flex flex-row justify-between gap-x-3">
            <StatCard
              color="text-greenlight"
              stat={`${formatNumber(
                metrics?.getGreaseTrapStats?.totals.gallons_grease_received ||
                  0,
              )} Gal`}
              subtitle={t('vendor-grease-traps.graph1-subtitle')}
              type="small"
            />
            <StatCard
              color="text-greenlight"
              stat={formatNumberMoney(
                metrics?.getGreaseTrapStats?.totals.total_amount || 0,
              )}
              subtitle={t('vendor-grease-traps.graph2-subtitle')}
              type="small"
            />
            <StatCard
              color="text-greenlight"
              stat={formatNumberMoney(
                metrics?.getGreaseTrapStats?.totalPayments || 0,
              )}
              subtitle={t('vendor-grease-traps.graph3-subtitle')}
              type="small"
            />
          </div>
        </div>
        <div className="w-full pt-6">
          <h2 className="text-xl font-semibold">
            {t('vendor-grease-traps.logs-title')}
          </h2>
          <div className="mb-10 flex w-full flex-col gap-y-5">
            {greaseTraps && greaseTraps.length === 0 && !loading && (
              <div className="flex justify-center">
                <p className="text-center text-xs italic">
                  {t('vendor-overall.no-timeline-items')}
                </p>
              </div>
            )}
            {loading && <LoaderSmall />}
            {greaseTraps.map((greaseTrap) => (
              <GreaseTrapCard
                key={greaseTrap.id}
                vendorId={vendorInfo.id}
                vendorName={vendorInfo.name}
                greaseTrap={greaseTrap}
                loadingSingleQuery={loadingSingleQuery}
                refetchSingleGreaseTrap={refetchSingleGreaseTrap}
                dropdownOptions={options?.getGreaseTrapOptions}
              />
            ))}
            {(loading || hasNextPage) && (
              <SentryInfiniteScroll ref={sentryRef}>
                <LoaderSmall />
              </SentryInfiniteScroll>
            )}
          </div>
        </div>
      </div>
    </SingleVendorLayout>
  );
};

export default VendorGreaseTrapsView;
